import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { t } from '@/i18n';
import { useMileageApi } from '@/services/api';
import { AddressInput } from './AddressInput';
import type {
  StandardRouteListItem,
  VehicleListItem,
  StructuredAddress,
  CalculateDistanceResult,
} from '@ip/shared-types';

const COLUMN_COUNT = 6;
const NO_VEHICLE = '__none__';

interface StandardRouteManagementProps {
  companyNip: string;
  vehicles: VehicleListItem[];
  companyAddress?: StructuredAddress;
}

interface RouteFormState {
  vehicleId: string | undefined;
  routeFromAddress: StructuredAddress;
  routeToAddress: StructuredAddress;
  distanceKm: string;
  defaultPurpose: string;
  isRoundTrip: boolean;
}

export function StandardRouteManagement({ companyNip, vehicles, companyAddress }: StandardRouteManagementProps) {
  const [routes, setRoutes] = useState<StandardRouteListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState<StandardRouteListItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [routeAlternatives, setRouteAlternatives] = useState<CalculateDistanceResult[]>([]);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState(0);
  const [searchingPlace, setSearchingPlace] = useState(false);
  const [form, setForm] = useState<RouteFormState>({
    vehicleId: undefined,
    routeFromAddress: { country: 'Polska' },
    routeToAddress: { country: 'Polska' },
    distanceKm: '0',
    defaultPurpose: '',
    isRoundTrip: true,
  });

  const { getRoutes, createRoute, updateRoute, deleteRoute, calculateDistanceAll, searchPlace } = useMileageApi();

  const setField = <K extends keyof RouteFormState>(field: K, value: RouteFormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const data = await getRoutes(companyNip);
      setRoutes(data);
    } catch (error) {
      console.error('Failed to load routes:', error);
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNip]);

  const handleAdd = () => {
    setEditingRoute(null);
    setRouteAlternatives([]);
    setSelectedRouteIdx(0);
    setForm({
      vehicleId: undefined,
      routeFromAddress: companyAddress || { country: 'Polska' },
      routeToAddress: { country: 'Polska' },
      distanceKm: '0',
      defaultPurpose: '',
      isRoundTrip: true,
    });
    setEditModalOpen(true);
  };

  const handleEdit = (route: StandardRouteListItem) => {
    setEditingRoute(route);
    setRouteAlternatives([]);
    setSelectedRouteIdx(0);
    setForm({
      vehicleId: route.vehicleId || undefined,
      routeFromAddress: route.routeFromAddress || { country: 'Polska' },
      routeToAddress: route.routeToAddress || { country: 'Polska' },
      distanceKm: String(route.distanceKm),
      defaultPurpose: route.defaultPurpose || '',
      isRoundTrip: route.isRoundTrip,
    });
    setEditModalOpen(true);
  };

  const formatAddr = (a: StructuredAddress) => {
    const parts: string[] = [];
    if (a.street) parts.push(`ul. ${a.street}${a.number ? ` ${a.number}` : ''}`);
    if (a.city) parts.push(a.city);
    if (parts.length === 0 && a.clientName) return a.clientName;
    return parts.join(', ') || '';
  };

  const handleSave = async () => {
    const distanceKm = Number(form.distanceKm);
    if (form.distanceKm === '' || Number.isNaN(distanceKm) || distanceKm <= 0) {
      toast.error(t('employees.validation.required', { label: t('mileage.routes.distanceKm') }));
      return;
    }
    try {
      setSaving(true);
      const fromAddr: StructuredAddress = form.routeFromAddress || {};
      const toAddr: StructuredAddress = form.routeToAddress || {};
      const payload = {
        vehicleId: form.vehicleId,
        routeFromAddress: fromAddr,
        routeToAddress: toAddr,
        distanceKm,
        defaultPurpose: form.defaultPurpose,
        isRoundTrip: form.isRoundTrip,
        routeFrom: formatAddr(fromAddr),
        routeTo: formatAddr(toAddr),
      };
      if (editingRoute) {
        await updateRoute(companyNip, editingRoute.id, payload);
      } else {
        await createRoute(companyNip, payload);
      }
      toast.success(t('common.save') + ' OK');
      setEditModalOpen(false);
      loadRoutes();
    } catch (error) {
      console.error('Failed to save route:', error);
      toast.error(t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (routeId: string) => {
    try {
      await deleteRoute(companyNip, routeId);
      toast.success(t('common.delete') + ' OK');
      loadRoutes();
    } catch (error) {
      console.error('Failed to delete route:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  const buildGeocodableAddress = (addr: StructuredAddress): string => {
    const parts: string[] = [];
    if (addr.street) parts.push(`${addr.street}${addr.number ? ` ${addr.number}` : ''}`);
    if (addr.city) parts.push(addr.city);
    if (addr.country) parts.push(addr.country);
    return parts.join(', ');
  };

  const handleCalculateDistance = async () => {
    const fromAddr: StructuredAddress = form.routeFromAddress || {};
    const toAddr: StructuredAddress = form.routeToAddress || {};
    const origin = buildGeocodableAddress(fromAddr);
    const destination = buildGeocodableAddress(toAddr);
    if (!origin || !destination) {
      toast.warning(t('mileage.routes.enterAddressesFirst'));
      return;
    }

    try {
      setCalculating(true);
      const results = await calculateDistanceAll(companyNip, {
        origin,
        destination,
        computeAlternatives: true,
      });
      setRouteAlternatives(results);
      setSelectedRouteIdx(0);
      // Set first route's distance
      const km = form.isRoundTrip
        ? Math.round(results[0].distanceKm * 2 * 10) / 10
        : results[0].distanceKm;
      setField('distanceKm', String(km));
      toast.success(
        t('mileage.routes.routesFound', { count: results.length, km: results[0].distanceKm }),
      );
    } catch (error) {
      console.error('Failed to calculate distance:', error);
      toast.error(t('mileage.routes.calculateFailed'));
    } finally {
      setCalculating(false);
    }
  };

  const handleSelectRoute = (idx: number) => {
    setSelectedRouteIdx(idx);
    const route = routeAlternatives[idx];
    if (route) {
      const km = form.isRoundTrip
        ? Math.round(route.distanceKm * 2 * 10) / 10
        : route.distanceKm;
      setField('distanceKm', String(km));
    }
  };

  const handleSearchPlace = async (query: string) => {
    try {
      setSearchingPlace(true);
      const results = await searchPlace(companyNip, query);
      if (results.length === 0) {
        toast.info(t('mileage.routes.noPlacesFound'));
        return;
      }
      // Use the first result to populate the To address
      const place = results[0];
      setForm(prev => {
        const current: StructuredAddress = prev.routeToAddress || {};
        return {
          ...prev,
          routeToAddress: {
            ...current,
            city: place.address.city || current.city,
            street: place.address.street || current.street,
            number: place.address.number || current.number,
            country: place.address.country || current.country || 'Polska',
            clientName: place.address.clientName || current.clientName,
          },
        };
      });
      toast.success(t('mileage.routes.addressFound', { address: place.formattedAddress }));
    } catch (error) {
      console.error('Failed to search place:', error);
      toast.error(t('mileage.routes.searchFailed'));
    } finally {
      setSearchingPlace(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="font-medium">{t('mileage.routes.title')}</span>
        <Button size="sm" onClick={handleAdd}>
          <Plus className="size-4" />
          {t('mileage.routes.add')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[160px]">
                {t('mileage.routes.routeFrom')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[160px]">
                {t('mileage.routes.routeTo')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[100px] text-right">
                {t('mileage.routes.distanceKm')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[80px] text-center">
                {t('mileage.routes.isRoundTrip')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[200px]">
                {t('mileage.routes.defaultPurpose')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[100px]">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : routes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {t('mileage.routes.noRoutes')}
                </TableCell>
              </TableRow>
            ) : (
              routes.map(route => (
                <TableRow key={route.id}>
                  <TableCell className="py-2">{route.routeFrom}</TableCell>
                  <TableCell className="py-2">{route.routeTo}</TableCell>
                  <TableCell className="py-2 text-right tabular-nums">{route.distanceKm} km</TableCell>
                  <TableCell className="py-2 text-center">
                    {route.isRoundTrip ? <Badge>RT</Badge> : <Badge variant="outline">OW</Badge>}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate py-2">
                    {route.defaultPurpose || '-'}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => handleEdit(route)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive size-8">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this route?</AlertDialogTitle>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-white hover:bg-destructive/90"
                              onClick={() => handleDelete(route.id)}
                            >
                              {t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingRoute ? t('mileage.routes.editTitle') : t('mileage.routes.add')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="route-vehicle">Vehicle (optional)</Label>
              <Select
                value={form.vehicleId ?? NO_VEHICLE}
                onValueChange={v => setField('vehicleId', v === NO_VEHICLE ? undefined : v)}
              >
                <SelectTrigger id="route-vehicle" className="w-full">
                  <SelectValue placeholder="All vehicles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_VEHICLE}>All vehicles</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {`${v.displayName} (${v.registrationNumber})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('mileage.routes.routeFrom')}</Label>
              <AddressInput
                value={form.routeFromAddress}
                onChange={value => setField('routeFromAddress', value)}
                defaultAddress={companyAddress}
                showClientName={false}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('mileage.routes.routeTo')}</Label>
              <AddressInput
                value={form.routeToAddress}
                onChange={value => setField('routeToAddress', value)}
                showClientName
                defaultAddress={companyAddress}
                onSearchPlace={handleSearchPlace}
                searchLoading={searchingPlace}
              />
            </div>
            <div className="flex items-end gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="route-distance">{t('mileage.routes.distanceKm')}</Label>
                <div className="flex w-[150px]">
                  <Input
                    id="route-distance"
                    type="number"
                    min={0.1}
                    step={0.1}
                    className="rounded-r-none"
                    value={form.distanceKm}
                    onChange={e => setField('distanceKm', e.target.value)}
                  />
                  <span className="border-input bg-muted text-muted-foreground inline-flex items-center rounded-r-md border border-l-0 px-3 text-sm">
                    km
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="route-round-trip">{t('mileage.routes.isRoundTrip')}</Label>
                <div className="flex h-9 items-center">
                  <Switch
                    id="route-round-trip"
                    checked={form.isRoundTrip}
                    onCheckedChange={checked => setField('isRoundTrip', checked)}
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mb-0.5"
                onClick={handleCalculateDistance}
                disabled={calculating}
              >
                {calculating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <MapPin className="size-4" />
                )}
                {t('mileage.routes.calculateDistance')}
              </Button>
            </div>

            {/* Route alternatives */}
            {routeAlternatives.length > 1 && (
              <div className="flex flex-col gap-1.5">
                <Label>Route options</Label>
                <RadioGroup
                  value={String(selectedRouteIdx)}
                  onValueChange={v => handleSelectRoute(Number(v))}
                  className="gap-2"
                >
                  {routeAlternatives.map((alt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <RadioGroupItem value={String(idx)} id={`route-alt-${idx}`} />
                      <label
                        htmlFor={`route-alt-${idx}`}
                        className="bg-card flex flex-1 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm"
                      >
                        <span className="font-medium">{alt.distanceKm} km</span>
                        <span className="text-muted-foreground">{alt.durationMinutes} min</span>
                        {alt.description && <Badge variant="outline">{alt.description}</Badge>}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="route-purpose">{t('mileage.routes.defaultPurpose')}</Label>
              <Input
                id="route-purpose"
                placeholder="e.g. Zakup materiałów biurowych"
                value={form.defaultPurpose}
                onChange={e => setField('defaultPurpose', e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving} onClick={handleSave}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
